import { useCallback, useRef } from 'react';
import { focusNavCell } from './gridNavigation';

/**
 * §1: «Tab и Enter переходят по полям, Enter в последнем поле создаёт новую строку». Tab already
 * works via native browser focus order (DOM order matches reading order in a `<table>`). This
 * hook only needs to intercept Enter: move to `[data-nav-row][data-nav-col+1]`, or the first cell
 * of the next row, or — on the very last nav-cell — call `onAddRow` and focus the new row's first
 * cell once it renders.
 *
 * `onAddRow` может вернуть `false`, если строку добавлять не стал (например, последняя строка и
 * так пустая и сам поставил на неё фокус) — тогда отложенный переход на «новую» строку отменяется,
 * иначе он сработал бы при следующем, совсем другом изменении числа строк.
 */
export function useGridNavigation(onAddRow: () => void | boolean) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pendingFocusRow = useRef<number | null>(null);

  const cellAt = useCallback(
    (row: number, col: number) => containerRef.current?.querySelector<HTMLElement>(`[data-nav-row="${row}"][data-nav-col="${col}"]`) ?? null,
    [],
  );

  const focusRow = useCallback((row: number) => focusNavCell(cellAt(row, 0)), [cellAt]);

  const focusCell = useCallback((row: number, col: number) => focusNavCell(cellAt(row, col)), [cellAt]);

  /** Строка, в которой сейчас стоит фокус, или `null` (фокус вне таблицы либо вне её ячеек). */
  const focusedRow = useCallback((): number | null => {
    const active = document.activeElement;
    if (!containerRef.current || !(active instanceof HTMLElement) || !containerRef.current.contains(active)) return null;
    const cell = active.closest<HTMLElement>('[data-nav-row]');
    return cell ? Number(cell.dataset.navRow) : null;
  }, []);

  /** Добавить строку и перевести фокус в её первую ячейку, когда она отрисуется. */
  const addRowAndFocus = useCallback(
    (newRowIndex: number) => {
      pendingFocusRow.current = newRowIndex;
      if (onAddRow() === false) pendingFocusRow.current = null;
    },
    [onAddRow],
  );

  const onKeyDownCapture = useCallback(
    (e: React.KeyboardEvent) => {
      // Только голый Enter: `Ctrl+Enter` («Провести»), `Alt+Enter` и т. п. — горячие клавиши окна,
      // и перехват с `preventDefault` лишил бы их срабатывания прямо из ячейки таблицы.
      if (e.key !== 'Enter' || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      const target = e.target as HTMLElement;
      const cell = target.closest<HTMLElement>('[data-nav-row]');
      if (!cell || !containerRef.current) return;
      // Let dropdown/select components consume Enter for their own selection first.
      if (target.getAttribute('role') === 'combobox' || target.closest('.ant-select-open')) return;

      e.preventDefault();
      const row = Number(cell.dataset.navRow);
      const col = Number(cell.dataset.navCol);

      const sameRowNext = cellAt(row, col + 1);
      if (sameRowNext) {
        focusNavCell(sameRowNext);
        return;
      }
      const nextRowFirst = cellAt(row + 1, 0);
      if (nextRowFirst) {
        focusNavCell(nextRowFirst);
        return;
      }
      addRowAndFocus(row + 1);
    },
    [cellAt, addRowAndFocus],
  );

  /** Call after a new row renders, to move focus into it (Enter-added row, §1). */
  const focusPendingRow = useCallback(() => {
    if (pendingFocusRow.current === null) return;
    const row = pendingFocusRow.current;
    pendingFocusRow.current = null;
    requestAnimationFrame(() => focusRow(row));
  }, [focusRow]);

  return { containerRef, onKeyDownCapture, focusPendingRow, focusRow, focusCell, focusedRow, addRowAndFocus };
}
